import React from 'react';
import { Grid, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useAccountData } from '../StorefrontThemeWrapper';
import { LanguageSwitcher } from '../../shared/ui/LanguageSwitcher';
import { AccountLogo } from '../../shared/ui/AccountLogo';

export const accountLogoStyle: React.CSSProperties = {
  maxHeight: 200,
  maxWidth: 300,
  objectFit: 'contain' as const,
};

export const StorefrontHeader = () => {
  const account = useAccountData();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { i18n } = useTranslation();
  const { accountId, storefrontId, orderPageId } = useParams();
  const [searchParams] = useSearchParams();

  const isExperiencePage = !!orderPageId;
  const storefrontQuery = searchParams.toString();
  const storefrontUrl = storefrontId
    ? `/storefront/${accountId}/${storefrontId}${storefrontQuery ? `?${storefrontQuery}` : ''}`
    : null;
  const BackArrow = i18n.dir() === 'rtl' ? ArrowForward : ArrowBack;

  const externalReferrer = (() => {
    const STORAGE_KEY = 'monkeybook-storefront-external-referrer';
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) return stored;
    try {
      if (!document.referrer) return null;
      const ref = new URL(document.referrer);
      if (ref.origin === window.location.origin) return null;
      if (ref.hostname.startsWith('link')) return null;
      sessionStorage.setItem(STORAGE_KEY, document.referrer);
      return document.referrer;
    } catch {
      return null;
    }
  })();
  const showExternalBack = !!externalReferrer && !(isExperiencePage && storefrontUrl);

  const languages = account?.customer_facing_widgets_languages || [];

  const logo = (
    <AccountLogo
      account={account}
      width={300}
      style={storefrontUrl ? { ...accountLogoStyle, cursor: 'pointer' } : accountLogoStyle}
      linkTo={storefrontUrl ?? undefined}
    />
  );

  if (isSmallScreen) {
    return (
      <Grid
        container
        direction="column"
        alignItems="center"
        sx={{
          padding: '16px 0',
          borderBottom: '1px solid #eee',
          gap: 1,
        }}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid sx={{ width: 48 }} container alignItems="center">
            {isExperiencePage && storefrontUrl && (
              <Link to={storefrontUrl} style={{ display: 'flex', alignItems: 'center' }}>
                <IconButton sx={{ color: 'text.primary' }}>
                  <BackArrow />
                </IconButton>
              </Link>
            )}
            {showExternalBack && (
              <IconButton
                sx={{ color: 'text.primary' }}
                onClick={() => {
                  window.location.href = externalReferrer!;
                }}>
                <BackArrow />
              </IconButton>
            )}
          </Grid>
          <Grid container sx={{ width: 'auto' }} justifyContent="flex-end">
            <LanguageSwitcher languages={languages} />
          </Grid>
        </Grid>
        <Grid container justifyContent="center">
          {logo}
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid
      container
      alignItems="center"
      justifyContent="space-between"
      sx={{
        padding: '24px 0',
        borderBottom: '1px solid #eee',
      }}>
      <Grid sx={{ width: 100 }} container alignItems="center">
        {isExperiencePage && storefrontUrl && (
          <Link to={storefrontUrl} style={{ display: 'flex', alignItems: 'center' }}>
            <IconButton sx={{ color: 'text.primary' }}>
              <BackArrow />
            </IconButton>
          </Link>
        )}
        {showExternalBack && (
          <IconButton
            sx={{ color: 'text.primary' }}
            onClick={() => {
              window.location.href = externalReferrer!;
            }}>
            <BackArrow />
          </IconButton>
        )}
      </Grid>
      <Grid container justifyContent="center" flex={1}>
        {logo}
      </Grid>
      <Grid container sx={{ width: 100 }} justifyContent="flex-end">
        <LanguageSwitcher languages={languages} />
      </Grid>
    </Grid>
  );
};
